import React, { useState } from 'react';
import { Formik, Form } from "formik";
import * as Yup from "yup";
import bcrypt from 'bcryptjs';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Login_bg from '../assests/images/login_bg.png'
import Logo from '../assests/images/Upjao_logo.png'
import Button from '@mui/material/Button';
import { successMessage, errorMessage } from '../components/Toaster';
import DialogModal from '../components/Dialog';
import { DASHBOARDADMIN, DASHBOARDREGIONADMIN, DASHBOARDOPERATOR, DASHBOARDEDITOR, SUPERADMIN } from "../constants";
import { login, changePassword } from '../utils/api';
import { getErrorMessage } from "../utils/error";

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isPasswordSet, setIsPasswordSet] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [setupToken, setSetupToken] = useState(null);

  const validationSchema = Yup.object({
    userName: Yup.string().required('User name is required'),
    password: Yup.string().required('Password is required'),
    oldPassword: Yup.string().when('isPasswordSet', {
      is: false,
      then: (schema) => schema.required('Old password is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    newPassword: Yup.string().when('isPasswordSet', {
      is: false,
      then: (schema) => schema.required('New password is required').min(8, 'Password must be at least 8 characters'),
      otherwise: (schema) => schema.notRequired(),
    }),
    confirmPassword: Yup.string().when('isPasswordSet', {
      is: false,
      then: (schema) =>
        schema.oneOf([Yup.ref('newPassword')], 'Passwords must match').required('Confirm password is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleLogin = async (values) => {
    try {
      setLoading(true);
      const response = await login({ user_name: values.userName, password: values.password });
      const {
        is_password_set,
        user_id,
        user_name,
        first_name,
        last_name,
        email,
        phone_number,
        company_id,
        company_name,
        role_name,
        jwt_token,
        location_name
      } = response.data;

      setLoading(false);

      if (role_name !== DASHBOARDADMIN && role_name !== DASHBOARDREGIONADMIN && role_name !== DASHBOARDOPERATOR && role_name !== DASHBOARDEDITOR && role_name !== SUPERADMIN) {
        errorMessage("Access denied: You do not have the required role.");
        return;
      }

      if (is_password_set) {
        localStorage.setItem("user_id", user_id);
        localStorage.setItem("user_name", user_name);
        localStorage.setItem("company_id", company_id);
        localStorage.setItem("company_name", company_name);
        localStorage.setItem("auth_token", jwt_token);
        localStorage.setItem("first_name", first_name);
        localStorage.setItem("last_name", last_name);
        localStorage.setItem("email", email || "");
        localStorage.setItem("phone_number", phone_number || "");
        localStorage.setItem("role_name", role_name);
        localStorage.setItem("location_name", location_name ?? "");

        const redirectTo = localStorage.getItem("redirectAfterLogin");
        if (redirectTo) {
          localStorage.removeItem("redirectAfterLogin");
          const updatedRedirect = redirectTo.replace("protected=true", "protected=false");
          navigate(updatedRedirect);
        } else {
          navigate("/home");
        }
      } else {
        setSetupToken(jwt_token);
        setIsPasswordSet(false);
      }
    } catch (error) {
      setLoading(false);
      const message = getErrorMessage(error);
      errorMessage(message || 'Login failed.');
    }
  };

  const handleChangePassword = async (values) => {
    if (values.oldPassword === values.newPassword) {
      errorMessage('New password cannot be the same as the old password.');
      return;
    }

    if (values.newPassword !== values.confirmPassword) {
      errorMessage('New password and confirm password must be the same.');
      return;
    }

    try {
      setLoading(true);
      const hashedPassword = await bcrypt.hash(values.newPassword, 10);
      const response = await changePassword({
        user_name: values.userName,
        old_password: values.oldPassword,
        new_password: hashedPassword,
        token: setupToken,
      });
      setLoading(false);
      successMessage(response.message);
      setIsPasswordSet(true);
    } catch (error) {
      setLoading(false);
      const message = getErrorMessage(error);
      errorMessage(message || 'Password change failed.');
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  return (
    <div className="login-page">
      <div className="login-shell">
        {/* LEFT: hero panel */}
        <div className="login-hero" style={{ backgroundImage: `url(${Login_bg})` }}>
          <div className="login-hero-tint" />
          <span className="login-dot-cluster login-dot-cluster--top" />
          <span className="login-dot-cluster login-dot-cluster--bottom" />
          <div className="login-hero-content">
            <div className="login-logo">
              <img src={Logo} alt="Upjao logo" className="login-logo-mark" />
            </div>

            <div className="login-hero-copy">
              <h1 className="APP-COLOR">
                Smart Agriculture.<br />
                <span className="APP-COLOR-2">Better Tomorrow.</span>
              </h1>
              <div className="login-hero-rule-row">
                <span className="login-hero-rule" />
                <span className="login-hero-dot" />
              </div>
              <p>Empowering farmers with data-driven insights for a sustainable future.</p>
            </div>
          </div>
        </div>


        {/* RIGHT: form card */}
        <div className="login-form-panel">
          <Formik
            initialValues={{
              userName: '',
              password: '',
              oldPassword: '',
              newPassword: '',
              confirmPassword: '',
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              if (isPasswordSet) {
                handleLogin(values);
              } else {
                handleChangePassword(values);
              }
            }}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
              handleSubmit,
              resetForm
            }) => (
              <Form onSubmit={handleSubmit} className="login-card">
                <div className="login-card-icon">
                  <LockOutlinedIcon fontSize="medium" />
                </div>

                <h2>{isPasswordSet ? 'Welcome Back!' : 'Change Password'}</h2>
                <p className="login-card-subtitle">
                  {isPasswordSet
                    ? 'Please sign in to continue to your account.'
                    : 'Enter details to change your password.'}
                </p>

                <TextField
                  className="login-field"
                  label="Username"
                  onChange={(e) => setFieldValue('userName', e.target.value)}
                  variant="outlined"
                  value={values.userName}
                  error={touched.userName && Boolean(errors.userName)}
                  helperText={touched.userName && errors.userName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                {isPasswordSet ? (
                  <TextField
                    className="login-field"
                    label="Password"
                    onChange={(e) => setFieldValue('password', e.target.value)}
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    value={values.password}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((s) => !s)} edge="end" size="small">
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <>
                    <TextField
                      required
                      className="login-field"
                      label="Old Password"
                      onChange={(e) => setFieldValue('oldPassword', e.target.value)}
                      variant="outlined"
                      type={showOldPassword ? 'text' : 'password'}
                      value={values.oldPassword}
                      error={touched.oldPassword && Boolean(errors.oldPassword)}
                      helperText={touched.oldPassword && errors.oldPassword}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlinedIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowOldPassword((s) => !s)} edge="end" size="small">
                              {showOldPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      required
                      className="login-field"
                      label="New Password"
                      onChange={(e) => setFieldValue('newPassword', e.target.value)}
                      variant="outlined"
                      type={showNewPassword ? 'text' : 'password'}
                      value={values.newPassword}
                      error={touched.newPassword && Boolean(errors.newPassword)}
                      helperText={touched.newPassword && errors.newPassword}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlinedIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowNewPassword((s) => !s)} edge="end" size="small">
                              {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      required
                      className="login-field"
                      label="Confirm Password"
                      onChange={(e) => setFieldValue('confirmPassword', e.target.value)}
                      variant="outlined"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={values.confirmPassword}
                      error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                      helperText={touched.confirmPassword && errors.confirmPassword}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlinedIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirmPassword((s) => !s)} edge="end" size="small">
                              {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}

                <Button
                  className="login-submit"
                  color="success"
                  variant="contained"
                  type="submit"
                  disabled={loading}
                >
                  <span className="login-submit-label">
                    {loading ? 'Processing...' : isPasswordSet ? 'Login' : 'Change Password'}
                    {!loading && <ArrowForwardIcon fontSize="small" />}
                  </span>
                </Button>

                <p className="login-footer-text">
                  {isPasswordSet ? (
                    <>New here? <button type="button" className="login-link" onClick={handleOpenModal}>Contact administrator</button></>
                  ) : (
                    <>
                      Back to login?{' '}
                      <button
                        type="button"
                        className="login-link"
                        onClick={() => {
                          setIsPasswordSet(true);
                          resetForm();
                        }}
                      >
                        Login
                      </button>
                    </>
                  )}
                </p>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <DialogModal
        open={openModal}
        onClose={handleCloseModal}
        onConfirm={handleCloseModal}
        title="Contact your Administrator"
        message="Please reach out to your administrator for assistance."
        confirmButtonText="OK"
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export default Login;